import { z } from 'zod'

import { ModuleTypeEnum } from './module-type'
import {
  WorkflowActionTriggerTimeEnum,
  WorkflowActionTypeEnum,
  WorkflowConditionLogicEnum,
  WorkflowConditionOperatorEnum,
  WorkflowConditionValueTypeEnum,
  WorkflowDefinitionInputSchema,
  WorkflowStatusEnum,
  WorkflowTriggerTypeEnum,
} from './workflow'

type JsonSchema = Record<string, unknown>

export const WORKFLOW_MCP_MODULE_TYPES = [
  ModuleTypeEnum.ENTITY,
  ModuleTypeEnum.CONTACT,
  ModuleTypeEnum.TASK,
  ModuleTypeEnum.DEAL,
  ModuleTypeEnum.PROJECT,
  ModuleTypeEnum.INVENTORY,
  ModuleTypeEnum.INVOICE,
  ModuleTypeEnum.EXPENSE,
  ModuleTypeEnum.FORM,
  ModuleTypeEnum.LEAD,
] as const

export const WORKFLOW_MCP_LOOKUP_MODULE_TYPES = [
  ModuleTypeEnum.CONTACT,
  ModuleTypeEnum.DEAL,
  ModuleTypeEnum.PROJECT,
  ModuleTypeEnum.TASK,
  ModuleTypeEnum.INVENTORY,
  ModuleTypeEnum.INVOICE,
  ModuleTypeEnum.LEAD,
] as const

export const WORKFLOW_MCP_FIND_RECORD_FIELDS: Partial<
  Record<ModuleTypeEnum, string[]>
> = {
  [ModuleTypeEnum.PROJECT]: ['id', 'name', 'code', 'moduleId', 'entityId'],
  [ModuleTypeEnum.ENTITY]: ['id', 'name', 'email', 'website', 'lifecycleStage'],
  [ModuleTypeEnum.LEAD]: [
    'id',
    'email',
    'companyName',
    'firstName',
    'lastName',
    'title',
    'lifecycleStage',
    'website',
  ],
  [ModuleTypeEnum.CONTACT]: [
    'id',
    'email',
    'companyName',
    'firstName',
    'lastName',
    'title',
    'lifecycleStage',
    'website',
  ],
  [ModuleTypeEnum.DEAL]: [
    'id',
    'name',
    'stage',
    'entityId',
    'primaryContactId',
  ],
  [ModuleTypeEnum.TASK]: ['id', 'name', 'taskNumber', 'moduleId', 'status'],
  [ModuleTypeEnum.INVENTORY]: ['id', 'name', 'sku', 'status'],
  [ModuleTypeEnum.INVOICE]: ['id', 'invoiceNumber', 'title', 'status'],
}

const enumValues = <T extends z.ZodEnum<[string, ...string[]]>>(schema: T) =>
  schema.options

const conditionSchema: JsonSchema = {
  type: 'object',
  properties: {
    field: {
      type: 'string',
      description:
        'Dot path resolved against { event, payload }, for example payload.record.name or payload.changes.lifecycleStage.to.',
    },
    operator: {
      type: 'string',
      enum: enumValues(WorkflowConditionOperatorEnum),
    },
    value: {
      description:
        'Comparison value. Omit for is_empty, is_not_empty, is_true, and is_false.',
    },
    valueType: {
      type: 'string',
      enum: enumValues(WorkflowConditionValueTypeEnum),
      description:
        'Optional cast used for number, boolean, date, and datetime comparisons.',
    },
    lookup: {
      type: 'object',
      description:
        'Optional record lookup before reading field. Use id or idPath to identify the record.',
      properties: {
        moduleType: {
          type: 'string',
          enum: WORKFLOW_MCP_LOOKUP_MODULE_TYPES,
        },
        idPath: {
          type: 'string',
          description: 'Path to a record ID, e.g. payload.record.id.',
        },
        id: { type: 'string', description: 'Record UUID.' },
      },
      required: ['moduleType'],
    },
  },
  required: ['field', 'operator'],
  additionalProperties: false,
}

export const WORKFLOW_MCP_CONDITION_GROUP_SCHEMA: JsonSchema = {
  type: 'object',
  properties: {
    logic: { type: 'string', enum: enumValues(WorkflowConditionLogicEnum) },
    conditions: { type: 'array', items: conditionSchema, default: [] },
    groups: {
      type: 'array',
      description: 'Nested condition groups with the same shape.',
      items: { type: 'object' },
      default: [],
    },
    label: { type: 'string' },
  },
  required: ['logic'],
}

export const WORKFLOW_MCP_TRIGGER_INPUT_SCHEMA: JsonSchema = {
  type: 'object',
  properties: {
    name: { type: 'string' },
    triggerType: {
      type: 'string',
      enum: enumValues(WorkflowTriggerTypeEnum),
    },
    config: {
      type: 'object',
      description:
        'Trigger-specific setup. See workflow_capabilities.triggerNodes for supported fields.',
    },
    conditions: {
      ...WORKFLOW_MCP_CONDITION_GROUP_SCHEMA,
      nullable: true,
    },
    isActive: { type: 'boolean', default: true },
  },
  required: ['name', 'triggerType'],
}

export const WORKFLOW_MCP_ACTION_INPUT_SCHEMA: JsonSchema = {
  type: 'object',
  properties: {
    name: { type: 'string' },
    actionType: {
      type: 'string',
      enum: enumValues(WorkflowActionTypeEnum),
    },
    triggerTime: {
      type: 'string',
      enum: enumValues(WorkflowActionTriggerTimeEnum),
      default: 'ON_TRIGGER',
    },
    config: {
      type: 'object',
      description:
        'Action-specific setup. See workflow_capabilities.actionNodes for supported fields.',
    },
    conditions: {
      ...WORKFLOW_MCP_CONDITION_GROUP_SCHEMA,
      nullable: true,
    },
    isActive: { type: 'boolean', default: true },
  },
  required: ['name', 'actionType'],
}

export const WORKFLOW_MCP_DEFINITION_INPUT_SCHEMA: JsonSchema = {
  type: 'object',
  properties: {
    triggers: {
      type: 'array',
      items: WORKFLOW_MCP_TRIGGER_INPUT_SCHEMA,
      default: [],
    },
    actions: {
      type: 'array',
      items: WORKFLOW_MCP_ACTION_INPUT_SCHEMA,
      default: [],
    },
  },
}

export const WORKFLOW_MCP_WRITE_INPUT_SCHEMA: JsonSchema = {
  type: 'object',
  properties: {
    name: { type: 'string' },
    description: { type: 'string' },
    status: {
      type: 'string',
      enum: enumValues(WorkflowStatusEnum),
      default: 'DRAFT',
    },
    settings: {
      type: 'object',
      properties: {
        scope: {
          type: 'object',
          properties: {
            moduleTypes: {
              type: 'array',
              items: { type: 'string', enum: WORKFLOW_MCP_MODULE_TYPES },
            },
            moduleId: { type: 'string' },
          },
        },
        version: { type: 'number' },
        tags: { type: 'array', items: { type: 'string' } },
      },
    },
    definition: WORKFLOW_MCP_DEFINITION_INPUT_SCHEMA,
    isActive: {
      type: 'boolean',
      description: 'Legacy alias: true maps to ACTIVE, false maps to PAUSED.',
    },
    trigger: {
      ...WORKFLOW_MCP_TRIGGER_INPUT_SCHEMA,
      description: 'Legacy alias for one definition trigger.',
    },
    conditions: {
      ...WORKFLOW_MCP_CONDITION_GROUP_SCHEMA,
      description: 'Legacy alias applied to trigger.conditions.',
    },
    actions: {
      type: 'array',
      items: WORKFLOW_MCP_ACTION_INPUT_SCHEMA,
      description: 'Legacy alias for definition.actions.',
    },
  },
}

const moduleTypeSchema = {
  type: 'string',
  enum: WORKFLOW_MCP_MODULE_TYPES,
}

const moduleTypesSchema = {
  type: 'array',
  items: moduleTypeSchema,
}

export const WORKFLOW_MCP_TRIGGER_NODES = [
  {
    type: 'MODULE_EVENT',
    label: 'Module Event',
    description: 'Runs when a record is created or updated.',
    eventTypes: ['record.created', 'record.updated'],
    configSchema: {
      type: 'object',
      properties: {
        eventType: {
          type: 'string',
          enum: ['record.created', 'record.updated'],
        },
        moduleType: moduleTypeSchema,
        moduleTypes: moduleTypesSchema,
        moduleId: {
          type: 'string',
          description:
            'Optional record UUID. Ignored for record.created because the record does not exist yet.',
        },
      },
    },
    discoveryTools: [
      'custom_field_definition_list',
      'entity_list',
      'lead_list',
      'deal_list',
      'project_list',
      'task_list',
    ],
  },
  {
    type: 'SCHEDULED',
    label: 'Scheduled',
    description:
      'Runs from the workflow scheduler. Use cadence for UI-native schedules, intervalMinutes for simple minute intervals, or cron for cron expressions.',
    eventTypes: ['schedule.tick'],
    configSchema: {
      type: 'object',
      properties: {
        eventType: { type: 'string', default: 'schedule.tick' },
        cadence: {
          type: 'object',
          properties: {
            unit: {
              type: 'string',
              enum: ['MINUTE', 'HOUR', 'DAY', 'WEEK', 'MONTH', 'YEAR'],
            },
            interval: { type: 'number' },
            weekDays: {
              type: 'array',
              items: { type: 'number', minimum: 0, maximum: 6 },
            },
            monthDayType: {
              type: 'string',
              enum: ['SPECIFIC', 'RELATIVE'],
            },
            monthDay: { type: 'number', minimum: 1, maximum: 31 },
            monthRelativeWeek: { type: 'number', minimum: 1, maximum: 5 },
            monthRelativeDayOfWeek: { type: 'number', minimum: 0, maximum: 6 },
            yearMonth: { type: 'number', minimum: 1, maximum: 12 },
            yearDay: { type: 'number', minimum: 1, maximum: 31 },
            startAt: { type: 'string', description: 'ISO 8601 datetime.' },
          },
        },
        intervalMinutes: {
          type: 'number',
          minimum: 10,
          description: 'Simple interval; minimum is 10 minutes.',
        },
        cron: { type: 'string' },
        timezone: { type: 'string' },
        moduleType: moduleTypeSchema,
        moduleId: { type: 'string' },
      },
    },
  },
  {
    type: 'WEBHOOK',
    label: 'Webhook',
    description:
      'Runs from POST /workflows/webhooks/:organizationSlug/:path with x-workflow-secret.',
    eventTypes: ['webhook.received'],
    configSchema: {
      type: 'object',
      properties: {
        path: { type: 'string' },
        secret: { type: 'string' },
        eventType: { type: 'string', default: 'webhook.received' },
        moduleType: moduleTypeSchema,
        moduleId: { type: 'string' },
      },
      required: ['path', 'secret'],
    },
  },
  {
    type: 'FORM_SUBMISSION',
    label: 'Form Submission',
    description: 'Runs when a Scrambl form is submitted.',
    eventTypes: ['form.submitted'],
    configSchema: {
      type: 'object',
      properties: {
        formId: { type: 'string' },
        formName: { type: 'string' },
        createdModuleType: moduleTypeSchema,
      },
    },
    discoveryTools: ['form_list', 'form_get'],
  },
  {
    type: 'STAGE_CHANGE',
    label: 'Stage Change',
    description:
      'Runs when a lead, deal, entity, project, or expense changes stage.',
    eventTypes: ['stage.changed', 'status.changed'],
    configSchema: {
      type: 'object',
      properties: {
        moduleType: moduleTypeSchema,
        moduleTypes: moduleTypesSchema,
        fromStage: { type: 'string' },
        toStage: { type: 'string' },
        stageRules: {
          type: 'object',
          description:
            'Map of module type to { fromStage?, toStage? }. Values should be stage IDs from stages_list.',
        },
      },
    },
    discoveryTools: ['stages_list', 'deal_stages_list', 'lead_stages_list'],
  },
  {
    type: 'STATUS_CHANGE',
    label: 'Status Change',
    description:
      'Runs when task, project, inventory, or invoice status changes.',
    eventTypes: ['status.changed'],
    configSchema: {
      type: 'object',
      properties: {
        moduleType: moduleTypeSchema,
        moduleTypes: moduleTypesSchema,
        fromStatus: { type: 'string' },
        toStatus: { type: 'string' },
        fromStatusType: { type: 'string' },
        toStatusType: { type: 'string' },
      },
    },
    discoveryTools: ['project_statuses_get', 'task_list'],
  },
] as const

export const WORKFLOW_MCP_ACTION_NODES = [
  {
    type: 'FIND_RECORD',
    label: 'Find Record',
    description:
      'Looks up an existing record and writes payload.foundRecord plus payload.lastAction.',
    configSchema: {
      type: 'object',
      properties: {
        moduleType: moduleTypeSchema,
        field: { type: 'string' },
        value: { description: 'Literal value or template token.' },
        operator: { type: 'string', enum: ['equals', 'contains'] },
      },
      required: ['moduleType', 'field', 'value'],
    },
    supportedFields: WORKFLOW_MCP_FIND_RECORD_FIELDS,
  },
  {
    type: 'CREATE_RECORD',
    label: 'Create Record',
    description:
      'Creates an entity, lead, deal, project, inventory item, or invoice from config.data.',
    configSchema: {
      type: 'object',
      properties: {
        moduleType: moduleTypeSchema,
        data: { type: 'object' },
        templateId: {
          type: 'string',
          description:
            'Optional project template ID when moduleType is PROJECT.',
        },
      },
      required: ['moduleType', 'data'],
    },
  },
  {
    type: 'UPDATE_RECORD',
    label: 'Update Record',
    description:
      'Updates a record using config.moduleId or the triggering moduleId.',
    configSchema: {
      type: 'object',
      properties: {
        moduleType: moduleTypeSchema,
        moduleId: { type: 'string' },
        data: { type: 'object' },
      },
      required: ['moduleType', 'data'],
    },
  },
  {
    type: 'SEND_NOTIFICATION',
    label: 'Send Notification',
    description:
      'Sends in-app notifications to recipientIds or the event actor if omitted.',
    configSchema: {
      type: 'object',
      properties: {
        recipientIds: { type: 'array', items: { type: 'string' } },
        title: { type: 'string' },
        message: { type: 'string' },
      },
    },
    discoveryTools: ['member_list'],
  },
  {
    type: 'SEND_EMAIL',
    label: 'Send Email',
    description:
      'Sends a workflow email. to, subject, and body or html are required at runtime.',
    configSchema: {
      type: 'object',
      properties: {
        to: { description: 'String, comma-separated string, or string array.' },
        cc: { description: 'String, comma-separated string, or string array.' },
        bcc: {
          description: 'String, comma-separated string, or string array.',
        },
        subject: { type: 'string' },
        body: { type: 'string' },
        html: { type: 'string' },
      },
      required: ['to', 'subject'],
    },
  },
  {
    type: 'SEND_SMS',
    label: 'Send SMS',
    description:
      'Sends a transactional SMS only to verified workspace members with active consent.',
    configSchema: {
      type: 'object',
      properties: {
        recipientIds: { type: 'array', items: { type: 'string' } },
        recipientNames: { type: 'array', items: { type: 'string' } },
        message: { type: 'string' },
      },
      required: ['recipientIds', 'message'],
    },
    discoveryTools: ['member_list'],
  },
  {
    type: 'ASSIGN_OWNER',
    label: 'Assign Owner',
    description: 'Assigns an owner to a lead, deal, or task using ownerId.',
    configSchema: {
      type: 'object',
      properties: {
        moduleType: moduleTypeSchema,
        moduleId: { type: 'string' },
        ownerId: { type: 'string' },
        ownerName: { type: 'string' },
      },
      required: ['moduleType', 'ownerId'],
    },
    discoveryTools: ['member_list'],
  },
  {
    type: 'MOVE_STAGE',
    label: 'Move Stage',
    description: 'Moves a lead, deal, or entity to a stage.',
    configSchema: {
      type: 'object',
      properties: {
        moduleType: moduleTypeSchema,
        moduleId: { type: 'string' },
        stage: { type: 'string', description: 'Stage ID.' },
        stageTypeId: { type: 'string' },
      },
      required: ['moduleType', 'stage'],
    },
    discoveryTools: ['stages_list', 'deal_stages_list', 'lead_stages_list'],
  },
  {
    type: 'POST_NOTE',
    label: 'Post Note',
    description:
      'Creates a note attached to moduleType/moduleId. Use contentHtml for rich text.',
    configSchema: {
      type: 'object',
      properties: {
        moduleType: moduleTypeSchema,
        moduleId: { type: 'string' },
        title: { type: 'string' },
        contentHtml: { type: 'string' },
        visibility: { type: 'string' },
      },
      required: ['moduleType', 'contentHtml'],
    },
  },
  {
    type: 'CREATE_TASK',
    label: 'Create Task',
    description:
      'Creates a task from config.data. Supports the same core fields as task_create.',
    configSchema: {
      type: 'object',
      properties: { data: { type: 'object' } },
      required: ['data'],
    },
  },
  {
    type: 'DELEGATE_TO_AI_AGENT',
    label: 'Delegate to AI Agent',
    description:
      'Creates a task from config.data (same shape as CREATE_TASK) and assigns it to the AI agent in config.agentAccountId. The agent works the task via MCP; when it finishes, the result goes to a human review queue instead of completing directly.',
    configSchema: {
      type: 'object',
      properties: {
        agentAccountId: {
          type: 'string',
          description: 'AgentAccount id to delegate the task to',
        },
        data: {
          type: 'object',
          description: 'Task fields (name required); template tokens allowed',
        },
      },
      required: ['agentAccountId', 'data'],
    },
  },
  {
    type: 'CREATE_INVOICE',
    label: 'Create Invoice',
    description: 'Creates an invoice from config.data.',
    configSchema: {
      type: 'object',
      properties: { data: { type: 'object' } },
      required: ['data'],
    },
  },
  {
    type: 'UPDATE_INVENTORY',
    label: 'Update Inventory',
    description:
      'Updates an inventory item using itemId or the triggering moduleId.',
    configSchema: {
      type: 'object',
      properties: {
        itemId: { type: 'string' },
        itemName: { type: 'string' },
        data: { type: 'object' },
      },
      required: ['data'],
    },
  },
  {
    type: 'LOG_TIME',
    label: 'Log Time',
    description: 'Creates a time entry from config.data.',
    configSchema: {
      type: 'object',
      properties: { data: { type: 'object' } },
      required: ['data'],
    },
  },
] as const

export const WORKFLOW_MCP_CONDITION_FIELD_PATHS = [
  {
    value: 'event.eventType',
    label: 'Event type',
    group: 'Event metadata',
  },
  {
    value: 'event.moduleType',
    label: 'Module type',
    group: 'Event metadata',
  },
  {
    value: 'event.moduleId',
    label: 'Module record ID',
    group: 'Event metadata',
  },
  {
    value: 'event.actorId',
    label: 'Actor ID',
    group: 'Event metadata',
  },
  {
    value: 'payload.record.id',
    label: 'Trigger record ID',
    group: 'Trigger payload',
  },
  {
    value: 'payload.record.name',
    label: 'Trigger record name',
    group: 'Trigger payload',
  },
  {
    value: 'payload.changes.<field>.from',
    label: 'Changed field before value',
    group: 'Record changes',
  },
  {
    value: 'payload.changes.<field>.to',
    label: 'Changed field after value',
    group: 'Record changes',
  },
  {
    value: 'payload.fromStage',
    label: 'Previous stage or lifecycle',
    group: 'Stage changes',
  },
  {
    value: 'payload.toStage',
    label: 'New stage or lifecycle',
    group: 'Stage changes',
  },
  {
    value: 'payload.fromStatus',
    label: 'Previous status',
    group: 'Status changes',
  },
  {
    value: 'payload.toStatus',
    label: 'New status',
    group: 'Status changes',
  },
  {
    value: 'payload.foundRecord.exists',
    label: 'Found record exists',
    group: 'Find record output',
  },
  {
    value: 'payload.lastAction.recordId',
    label: 'Previous action record ID',
    group: 'Previous action',
  },
] as const

export const WORKFLOW_MCP_TEMPLATE_TOKENS = [
  '{{payload.record.id}}',
  '{{payload.record.name}}',
  '{{payload.record.email}}',
  '{{payload.record.company}}',
  '{{payload.changes.lifecycleStage.from}}',
  '{{payload.changes.lifecycleStage.to}}',
  '{{payload.lastAction.recordId}}',
  '{{payload.lastAction.recordName}}',
  '{{payload.foundRecord.recordId}}',
  '{{payload.foundRecord.recordName}}',
  '{{payload.createdRecord.recordId}}',
  '{{payload.createdProject.recordId}}',
] as const

export const WORKFLOW_MCP_DEFINITION_EXAMPLES = [
  {
    name: 'Notify owner when a high-value deal is created',
    definition: {
      triggers: [
        {
          name: 'Deal created',
          triggerType: 'MODULE_EVENT',
          config: {
            eventType: 'record.created',
            moduleType: ModuleTypeEnum.DEAL,
          },
          conditions: {
            logic: 'all',
            conditions: [
              {
                field: 'payload.record.amount',
                operator: 'greater_or_equal',
                value: 10000,
                valueType: 'number',
              },
            ],
          },
          isActive: true,
        },
      ],
      actions: [
        {
          name: 'Notify actor',
          actionType: 'SEND_NOTIFICATION',
          triggerTime: 'ON_TRIGGER',
          config: {
            title: 'High-value deal created',
            message: '{{payload.record.name}} is ready for review.',
          },
          isActive: true,
        },
      ],
    },
  },
  {
    name: 'Create follow-up task when a form submits',
    definition: {
      triggers: [
        {
          name: 'Form submitted',
          triggerType: 'FORM_SUBMISSION',
          config: { eventType: 'form.submitted' },
          isActive: true,
        },
      ],
      actions: [
        {
          name: 'Create follow-up task',
          actionType: 'CREATE_TASK',
          triggerTime: 'ON_TRIGGER',
          config: {
            data: {
              name: 'Follow up with {{payload.record.name}}',
              description: 'Submitted form {{payload.formId}}',
            },
          },
          isActive: true,
        },
      ],
    },
  },
] as const

export function getWorkflowMcpCapabilities() {
  return {
    statuses: enumValues(WorkflowStatusEnum),
    triggerTypes: enumValues(WorkflowTriggerTypeEnum),
    actionTypes: enumValues(WorkflowActionTypeEnum),
    triggerTimes: enumValues(WorkflowActionTriggerTimeEnum),
    conditionOperators: enumValues(WorkflowConditionOperatorEnum),
    conditionLogic: enumValues(WorkflowConditionLogicEnum),
    conditionValueTypes: enumValues(WorkflowConditionValueTypeEnum),
    moduleTypes: WORKFLOW_MCP_MODULE_TYPES,
    lookupModuleTypes: WORKFLOW_MCP_LOOKUP_MODULE_TYPES,
    definitionSchema: WORKFLOW_MCP_DEFINITION_INPUT_SCHEMA,
    triggerNodes: WORKFLOW_MCP_TRIGGER_NODES,
    actionNodes: WORKFLOW_MCP_ACTION_NODES,
    conditionSchema: WORKFLOW_MCP_CONDITION_GROUP_SCHEMA,
    conditionFieldPaths: WORKFLOW_MCP_CONDITION_FIELD_PATHS,
    templateTokens: WORKFLOW_MCP_TEMPLATE_TOKENS,
    graphConfig: {
      actionOrder: 'Set action.config.order to control execution order.',
      nodeKey:
        'Set config.graph.nodeKey and action config.graph.incomingNodeKeys to preserve visual graph reachability.',
      conditionalPaths:
        'Set action.config.path = { id, label, order, mode, isFallback, conditions } to group actions behind a condition path.',
      pathModes: ['CONTINUE', 'STOP'],
    },
    discoveryTools: [
      'member_list',
      'form_list',
      'form_get',
      'stages_list',
      'deal_stages_list',
      'lead_stages_list',
      'custom_field_definition_list',
      'project_statuses_get',
    ],
    definitionExamples: WORKFLOW_MCP_DEFINITION_EXAMPLES,
  }
}

export function normalizeWorkflowMcpWritePayload(data: unknown) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data

  const { isActive, trigger, conditions, actions, ...rest } = data as Record<
    string,
    any
  >
  const normalized: Record<string, any> = { ...rest }

  if (normalized.status === undefined && isActive !== undefined) {
    normalized.status = isActive ? 'ACTIVE' : 'PAUSED'
  }

  if (normalized.definition === undefined && (trigger || actions)) {
    const normalizedTrigger =
      trigger && typeof trigger === 'object'
        ? {
            name: trigger.name || 'MCP trigger',
            triggerType: trigger.triggerType || trigger.type || 'MODULE_EVENT',
            config: trigger.config || {},
            conditions: trigger.conditions ?? conditions ?? null,
            isActive: trigger.isActive ?? true,
          }
        : undefined

    normalized.definition = {
      triggers: normalizedTrigger ? [normalizedTrigger] : [],
      actions: Array.isArray(actions)
        ? actions.map((action: any) => ({
            name: action.name || 'MCP action',
            actionType: action.actionType || action.type,
            triggerTime: action.triggerTime || 'ON_TRIGGER',
            config: action.config || {},
            conditions: action.conditions ?? null,
            isActive: action.isActive ?? true,
          }))
        : [],
    }
  }

  return normalized
}

export function validateWorkflowMcpDefinition(definition: unknown) {
  const result = WorkflowDefinitionInputSchema.safeParse(definition)

  if (result.success) {
    return {
      valid: true,
      definition: result.data,
      errors: [],
    }
  }

  return {
    valid: false,
    errors: result.error.issues.map(issue => ({
      path: issue.path.join('.'),
      message: issue.message,
      code: issue.code,
    })),
  }
}
